import React, { useEffect, useRef, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../../components/Config/api.service';
import { FaPlus, FaArrowLeft, FaChevronDown, FaChevronUp, FaFileImport } from 'react-icons/fa';
import { ClipLoader } from 'react-spinners';
import { notify } from '../../../components/Utils/Notifications';
import { FiTrash2 } from 'react-icons/fi';
import ActionDropdown from '../../../components/Utils/ActionDropdown';
import EmployeeDeductionRow from './EmployeesDeduction';
import Modal from '../../../components/Utils/ModalShow';
import ImportMappingModal from '../../../components/Tools/ImportMappingModal';
import SheetSelectionModal from '../../../components/Tools/SheetSelectionModal';
import { useExcelMappingImport } from '../../../components/Tools/useExcelMappingImport';
import AmountInput from "../../../components/Utils/AmountInput";
import { normalizeEmployeeMatricule, normalizeEmployeeName } from './excelImportUtils';
import AccountSelectField from '../../Settings/Tabs/Account/AccountSelectField';

const dateUtils = {
    getCurrentMonth: () => {
        const date = new Date();
        return `${date.getMonth() + 1}/${date.getFullYear()}`;
    },

    getNextYearMonth: () => {
        const date = new Date();
        return `${date.getMonth() + 1}/${date.getFullYear() + 1}`;
    }
};

const DeductionForm = ({ id, closeEdit }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [employeeLoans, setEmployeeLoans] = useState([]);
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
    const [isAccountSectionExpanded, setIsAccountSectionExpanded] = useState(false);
    const [isEmployeeSectionExpanded, setIsEmployeeSectionExpanded] = useState(true);
    const [formData, setFormData] = useState({
        libelle: '',
        montant: '',
        description: '',
        account_debit: '',
        account_credit: '',
        not_computed_in_net: false,
        isActive: true
    });
    const [validationErrors, setValidationErrors] = useState({});
    const importInputRef = useRef(null);
    const formatMonthYear = (value) => {
        if (!value) return '';
        const [month, year] = value.split('/');
        if (!month || !year) return value;
        const paddedMonth = month.padStart(2, '0');
        return `${paddedMonth}/${year}`;
    };
    useEffect(() => {
        const loadDeductionData = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const response = await apiService.getElementById('/loan', id);
                const loan = response.data.loan;
                setFormData({
                    libelle: loan.name || '',
                    montant: loan.amount || '',
                    description: loan.description || '',
                });
                if (loan.employees_loans && loan.employees_loans.length > 0) {
                    setEmployeeLoans(loan.employees_loans.map(emp => ({
                        employee_select: { value: emp.employee_id, label: emp.employee_name || '' },
                        amount: emp.amount,
                        dateStart: emp.date_start,
                        dateEnd: emp.date_end
                    })));
                }
            } catch (error) {
                console.error('Error loading deduction data:', error);
                notify(t('deductionForm.fetchError'), '', 3);
            } finally {
                setLoading(false);
            }
        };
        loadDeductionData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleFormChange = (e) => {
        const { id, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: type === 'checkbox' ? checked : value
        }));
        if (validationErrors[id]) {
            setValidationErrors(prev => ({ ...prev, [id]: null }));
        }
    };

    const handleMontantChange = (newValue) => {
        setFormData(prev => ({ ...prev, montant: newValue }));
        if (validationErrors.montant) {
            setValidationErrors(prev => ({ ...prev, montant: null }));
        }
    };

    const addEmployeeLoan = () => {
        setEmployeeLoans([...employeeLoans, {
            employee_id: null,
            employee_name: '',
            amount: formData.montant,
            dateStart: dateUtils.getCurrentMonth(),
            dateEnd: dateUtils.getNextYearMonth()
        }]);
    };

    const removeEmployeeLoan = (index) => {
        setEmployeeLoans(employeeLoans.filter((_, i) => i !== index));
    };

    const handleEmployeeLoanChange = (index, field, value) => {
        console.log("Employee Loan Change:", index, field, value);
        const updatedLoans = [...employeeLoans];
        updatedLoans[index][field] = value;
        console.log("Updated Loans:", updatedLoans);
        setEmployeeLoans(updatedLoans);
    };

    const handleImportClick = () => {
        if (importInputRef.current) {
            importInputRef.current.click();
        }
    };

    const parseAmount = (value) => {
        if (typeof value === "number") return Number.isFinite(value) ? value : null;
        if (value === null || value === undefined) return null;
        const cleaned = String(value).replace(/\s/g, "").replace(",", ".");
        const parsed = parseFloat(cleaned);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const toMonthYear = (value) => {
        if (value === null || value === undefined || value === "") return null;
        const raw = String(value).trim();
        if (!raw) return null;
        const mmYYYY = raw.match(/^(\d{1,2})[/-](\d{4})$/);
        if (mmYYYY) return `${String(parseInt(mmYYYY[1], 10)).padStart(2, "0")}/${mmYYYY[2]}`;
        const ddMMYYYY = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
        if (ddMMYYYY) return `${String(parseInt(ddMMYYYY[2], 10)).padStart(2, "0")}/${ddMMYYYY[3]}`;
        const date = new Date(raw);
        if (!Number.isNaN(date.getTime())) {
            return `${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
        }
        return null;
    };

    const applyImportedRows = async (importedRows) => {
        const employeesResponse = await apiService.getElement('/employees?offset=0&limit=5000');
        const employees = employeesResponse.data?.employees || [];
        const employeeByName = new Map();
        const employeeByMatricule = new Map();

        employees.forEach((emp) => {
            const key = normalizeEmployeeName(emp.name);
            if (!employeeByName.has(key)) {
                employeeByName.set(key, []);
            }
            employeeByName.get(key).push(emp);

            const matriculeKey = normalizeEmployeeMatricule(emp.matricule);
            if (matriculeKey && !employeeByMatricule.has(matriculeKey)) {
                employeeByMatricule.set(matriculeKey, emp);
            }
        });

        const existingMap = new Map();
        employeeLoans.forEach((loan) => {
            const employeeId = loan.employee_select?.value;
            if (employeeId) {
                existingMap.set(employeeId, loan);
            }
        });

        const unresolved = [];
        importedRows.forEach((row) => {
            const matriculeKey = normalizeEmployeeMatricule(row.matricule);
            const nameKey = normalizeEmployeeName(row.employeeName);
            const matches = employeeByName.get(nameKey) || [];
            const employee = (matriculeKey && employeeByMatricule.get(matriculeKey)) || matches[0];

            if (!employee) {
                unresolved.push(row.matricule || row.employeeName || t('deductionForm.rowWithoutIdentifier'));
                return;
            }

            const mappedLoan = {
                employee_select: { value: employee.id, label: employee.name },
                amount: row.amount,
                dateStart: row.dateStart || dateUtils.getCurrentMonth(),
                dateEnd: row.dateEnd || dateUtils.getNextYearMonth()
            };
            existingMap.set(employee.id, mappedLoan);
        });

        setEmployeeLoans(Array.from(existingMap.values()));

        const importedCount = importedRows.length - unresolved.length;
        if (importedCount > 0) {
            notify(t('deductionForm.importedCount', { count: importedCount }), '', 2);
        }
        if (unresolved.length > 0) {
            notify(t('deductionForm.unresolvedCount', { count: unresolved.length, list: unresolved.slice(0, 5).join(', ') + (unresolved.length > 5 ? '...' : '') }), '', 4);
        }
        if (importedCount === 0 && unresolved.length > 0) {
            notify(t('deductionForm.noRowsImported'), '', 3);
        }
    };

    const excelImport = useExcelMappingImport({
        notify,
        modelFields: [
            { name: "matricule", string: t('deductionForm.matricule') },
            { name: "employeeName", string: t('deductionForm.employeeName') },
            { name: "amount", string: t('deductionForm.amountField') },
            { name: "dateStart", string: t('deductionForm.startDateField') },
            { name: "dateEnd", string: t('deductionForm.endDateField') },
        ],
        templateHeaders: [t('deductionForm.matricule'), t('deductionForm.employeeName'), t('deductionForm.amountField'), t('deductionForm.startDateTemplate'), t('deductionForm.endDateTemplate')],
        templateSheetName: "Import Deduction",
        templateFilename: "modele_import_deduction.xlsx",
        mapMappedRow: (row) => ({
            employeeName: String(row.employeeName || "").trim(),
            matricule: String(row.matricule || "").trim(),
            amount: parseAmount(row.amount),
            dateStart: toMonthYear(row.dateStart),
            dateEnd: toMonthYear(row.dateEnd),
        }),
        isMappedRowValid: (row) => (row.employeeName || row.matricule) && row.amount !== null,
        onImportRows: applyImportedRows,
    });

    const validateForm = () => {
        const errors = {};

        if (!formData.libelle.trim()) {
            errors.libelle = t('deductionForm.labelRequired');
        }
        if (!formData.montant) {
            errors.montant = t('deductionForm.amountRequired');
        } else if (isNaN(parseFloat(formData.montant)) || parseFloat(formData.montant) <= 0) {
            errors.montant = t('deductionForm.amountMustBePositive');
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            notify(t('deductionForm.fixFormErrors'), '', 3);
            return;
        }

        const deductionData = {
            name: formData.libelle,
            amount: parseFloat(formData.montant),
            description: formData.description,
            employees_loans: employeeLoans.map(loan => ({
                employee_id: loan.employee_select ? loan.employee_select.value : null,
                employee_name: loan.employee_select ? loan.employee_select.label : '',
                amount: parseFloat(loan.amount || formData.montant),
                date_start: formatMonthYear(loan.dateStart),
                date_end: formatMonthYear(loan.dateEnd)
            }))
        };

        console.log("Deduction Data:", deductionData);

        setLoading(true);
        try {
            if (id) {
                await apiService.updateElementById('/loan', id, deductionData);
                notify(t('basePrimeForm.updateSuccess'), '', 2);
            } else {
                await apiService.saveElement('/loan', deductionData);
                notify(t('basePrimeForm.createSuccess'), '', 2);
            }
            closeEdit();
        } catch (error) {
            console.error('Error submitting form:', error);
            notify(id ? t('deductionForm.updateError') : t('deductionForm.createError'), '', 3);
        } finally {
            setLoading(false);
        }
    };

    const handleArchive = async () => {
        setIsArchiveModalOpen(false);
        setLoading(true);
        try {
            const updatedFormData = {
                active: false,
            };
            await apiService.updateElementById(`/loan`, id, updatedFormData);
            notify(t('basePrimeForm.archiveSuccess'), '', 2);
            closeEdit();
        } catch (error) {
            console.error('Error Archivage deduction:', error);
            notify(t('deductionForm.archiveError'), '', 3);
        } finally {
            setLoading(false);
        }
    };

    const dropdownActions = [
        {
            label: t('basePrimeForm.archive'),
            onClick: () => setIsArchiveModalOpen(true),
            disabled: loading || !id,
            color: 'text-red-500',
            icon: <FiTrash2 size={16} />
        }
    ]

    const loadAccountOptions = async (inputValue) => {
        if (inputValue.length < 2) return [];
        try {
            const response = await apiService.getElement(`/accounting/accounts/search?q=${inputValue}`);
            return response.data.map((account) => ({
                value: account.code,
                label: `${account.code} - ${account.name}`,
                account: account,
            }));
        } catch (error) {
            console.error("Erreur lors de la recherche des comptes:", error);
            return [];
        }
    };

    return (
    <>
        <SheetSelectionModal
            isOpen={excelImport.showSheetSelection}
            sheetNames={excelImport.sheetNames}
            onSelect={excelImport.handleSheetSelect}
            onCancel={excelImport.handleCancelSheetSelection}
        />
        {excelImport.showMapping && (
            <ImportMappingModal
                columns={excelImport.fileColumns}
                modelFields={excelImport.modelFields}
                onDownloadTemplate={excelImport.handleDownloadTemplate}
                onConfirm={excelImport.handleConfirmMapping}
                onTest={excelImport.handleTestMapping}
                onCancel={excelImport.handleCancelMapping}
            />
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className='flex justify-between items-center mb-6'>
                <h1 className="text-2xl font-bold flex items-center">
                    <small className="mr-4 text-gray-400 cursor-pointer" onClick={closeEdit}><FaArrowLeft /></small>
                    {t('deductionForm.title')}
                </h1>
                <div>
                    <button type='button' className='bg-orange-200 text-black rounded-full btn mx-2' onClick={closeEdit}>{t('basePrimeForm.cancel')}</button>
                    <button type="submit" className='bg-[--color-primary] text-white hover:bg-purple-500 rounded-full btn' disabled={loading}>
                        {loading ? <ClipLoader color='white' size={20} /> : t('basePrimeForm.save')}
                    </button>
                    <ActionDropdown actions={dropdownActions} />
                </div>
            </div>

            {/* Section Informations générales */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="border-b border-gray-200 px-6 py-4">
                    <h3 className="text-lg font-semibold text-gray-800">{t('basePrimeForm.generalInfo')}</h3>
                    <p className="text-sm text-gray-600 mt-1">{t('deductionForm.basicDetails')}</p>
                </div>
                <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label htmlFor="libelle" className="block text-sm font-medium text-gray-700">
                                {t('deductionForm.label')}
                                {validationErrors.libelle && (
                                    <span className="text-red-500 text-xs ml-2">{validationErrors.libelle}</span>
                                )}
                            </label>
                            <input
                                type="text"
                                id="libelle"
                                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 ${validationErrors.libelle ? 'border-red-500' : ''}`}
                                placeholder={t('deductionForm.labelPlaceholder')}
                                value={formData.libelle}
                                onChange={handleFormChange}
                            />
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="montant" className="block text-sm font-medium text-gray-700">
                                {t('deductionForm.defaultAmount')}
                                {validationErrors.montant && (
                                    <span className="text-red-500 text-xs ml-2">{validationErrors.montant}</span>
                                )}
                            </label>
                            <AmountInput
                                value={formData.montant}
                                onChange={handleMontantChange}
                                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 ${validationErrors.montant ? 'border-red-500' : ''}`}
                                placeholder={t('deductionForm.amountPlaceholder')}
                            />
                            <p className="text-xs text-gray-500">
                                {t('deductionForm.defaultAmountHelp')}
                            </p>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">{t('deductionForm.description')}</label>
                        <textarea
                            id="description"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                            placeholder={t('deductionForm.descriptionPlaceholder')}
                            value={formData.description}
                            onChange={handleFormChange}
                            rows={3}
                        />
                    </div>
                </div>
            </div>

            {/* Section Gestion des employés */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div
                    className="border-b border-gray-200 px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                    onClick={() => setIsEmployeeSectionExpanded(!isEmployeeSectionExpanded)}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800">{t('deductionForm.employeeAssignment')}</h3>
                            <p className="text-sm text-gray-600 mt-1">{t('deductionForm.employeeAssignmentSubtitle')}</p>
                            {validationErrors.employeeLoans && (
                                <span className="text-red-500 text-xs">{validationErrors.employeeLoans}</span>
                            )}
                        </div>
                        <div className="text-gray-400">
                            {isEmployeeSectionExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                    </div>
                </div>
                {isEmployeeSectionExpanded && (
                    <div className="p-6">
                        <div className="rounded-lg border">
                            <table className="table w-full border-collapse">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th style={{ width: "35%" }} className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('deductionForm.employee')}</th>
                                        <th style={{ width: "25%" }} className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('deductionForm.amountField')}</th>
                                        <th style={{ width: "20%" }} className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('deductionForm.startColumn')}</th>
                                        <th style={{ width: "20%" }} className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('deductionForm.endColumn')}</th>
                                        <th style={{ width: "10%" }} className="px-4 py-3 text-center text-sm font-medium text-gray-700">{t('deductionForm.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {employeeLoans.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                                                <div className="flex flex-col items-center">
                                                    <p className="mb-2">{t('deductionForm.noEmployeeAssigned')}</p>
                                                    <p className="text-sm">{t('deductionForm.noEmployeeAssignedHelp')}</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        employeeLoans.map((loan, index) => (
                                            <EmployeeDeductionRow
                                                key={index}
                                                loan={loan}
                                                index={index}
                                                onChange={handleEmployeeLoanChange}
                                                onRemove={removeEmployeeLoan}
                                                defaultAmount={formData.montant}
                                            />
                                        ))
                                    )}
                                </tbody>
                            </table>

                            <div className="border-t bg-gray-50 p-4">
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        className="flex items-center text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-3 py-2 rounded-lg transition-colors duration-200"
                                        onClick={addEmployeeLoan}
                                    >
                                        <FaPlus className="mr-2 text-xs" /> {t('deductionForm.addEmployee')}
                                    </button>
                                    <button
                                        type="button"
                                        className="flex items-center text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors duration-200"
                                        onClick={handleImportClick}
                                    >
                                        <FaFileImport className="mr-2 text-xs" /> {t('deductionForm.importExcel')}
                                    </button>
                                    <input
                                        ref={importInputRef}
                                        type="file"
                                        accept=".xlsx,.xls,.csv"
                                        className="hidden"
                                        onChange={excelImport.handleImportFileChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Section Configuration comptable */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div
                    className="border-b border-gray-200 px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                    onClick={() => setIsAccountSectionExpanded(!isAccountSectionExpanded)}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800">{t('basePrimeForm.accountingConfig')}</h3>
                            <p className="text-sm text-gray-600 mt-1">{t('basePrimeForm.accountingConfigSubtitle')}</p>
                        </div>
                        <div className="text-gray-400">
                            {isAccountSectionExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                    </div>
                </div>
                {isAccountSectionExpanded && (
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <AccountSelectField
                                label={t('basePrimeForm.debitAccount')}
                                value={formData.account_debit}
                                onChange={(option) => setFormData(prev => ({ ...prev, account_debit: option ? option.value : '' }))}
                                placeholder={t('basePrimeForm.search')}
                                loadAccountOptions={loadAccountOptions}
                            />
                            <AccountSelectField
                                label={t('basePrimeForm.creditAccount')}
                                value={formData.account_credit}
                                onChange={(option) => setFormData(prev => ({ ...prev, account_credit: option ? option.value : '' }))}
                                placeholder={t('basePrimeForm.search')}
                                loadAccountOptions={loadAccountOptions}
                            />
                        </div>

                        {/* Séparateur visuel */}
                        <div className="border-t border-gray-200 pt-6">
                            <h4 className="text-md font-medium text-gray-800 mb-4">{t('basePrimeForm.calculationOptions')}</h4>
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                <div>
                                    <h5 className="font-medium text-gray-800">{t('basePrimeForm.notComputedInNet')}</h5>
                                    <p className="text-sm text-gray-600 mt-1">{t('deductionForm.notComputedInNetHelp')}</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        name="not_computed_in_net"
                                        checked={!!formData.not_computed_in_net}
                                        onChange={e => setFormData(prev => ({ ...prev, not_computed_in_net: e.target.checked }))}
                                    />
                                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                </label>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </form>

        <Modal
            isOpen={isArchiveModalOpen}
            onClose={() => setIsArchiveModalOpen(false)}
            title={t('basePrimeForm.archiveConfirmTitle')}
            footer={
                <>
                    <button className="btn" onClick={() => setIsArchiveModalOpen(false)}>{t('basePrimeForm.cancel')}</button>
                    <button className="btn btn-error" onClick={handleArchive}>{t('basePrimeForm.confirm')}</button>
                </>
            }
        >
            <p>{t('noteFrais.archiveConfirmQuestion')}</p>
        </Modal>
    </>
    );
};

export default DeductionForm;
